package com.arenaproplay.admin.ui.components.charts.svg

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.attrsModifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div

/**
 * Multi-series smoothed area chart with Catmull-Rom→Bezier spline, per-series
 * gradient fills, annotation markers, and a dark-glass hover tooltip.
 *
 * Ported from kontaq QpAreaChart (PlatformCharts.kt) with Night Arena palette.
 *
 * @param series       one or more data series; first series gets the terminal dot
 * @param labels       X-axis labels (one per data point); must match series value count
 * @param heightPx     SVG height in pixels (defaults to 170 as in the mockup)
 * @param annotations  optional dashed vertical markers with captions
 * @param modifier     standard Kobweb modifier
 */
@Composable
fun AreaChart(
    series: List<ChartSeries>,
    labels: List<String>,
    heightPx: Int = 170,
    annotations: List<ChartAnnotation> = emptyList(),
    modifier: Modifier = Modifier,
) {
    if (series.isEmpty() || labels.isEmpty()) {
        EmptyChartStub(heightPx, modifier)
        return
    }

    // Hover state: -1 = none
    var hoverIdx by remember { mutableStateOf(-1) }
    val n = labels.size

    val svg = buildAreaChartSvg(series, labels, heightPx, annotations, hoverIdx)

    Box(modifier.fillMaxWidth().position(Position.Relative)) {
        // SVG layer
        Div(
            attrs = {
                style {
                    property("width", "100%")
                    property("line-height", "0")
                }
                ref { el ->
                    el.innerHTML = svg
                    onDispose { el.innerHTML = "" }
                }
            }
        )

        // Hover hit-strips: invisible full-height strips, one per X index.
        // Approach ported from kontaq QpAreaChart overlay Row.
        Row(
            Modifier
                .fillMaxWidth()
                .height(heightPx.px)
                .position(Position.Absolute)
                .top(0.px)
                .left(0.px)
        ) {
            (0 until n).forEach { i ->
                Box(
                    Modifier
                        .styleModifier { property("flex", "1") }
                        .fillMaxHeight()
                        .attrsModifier {
                            onMouseEnter { hoverIdx = i }
                            onMouseLeave { hoverIdx = -1 }
                        }
                )
            }
        }

        // Floating tooltip — positioned as a percentage of width so it follows the hover strip
        if (hoverIdx in 0 until n) {
            val rawPct = if (n <= 1) 50.0 else 100.0 * hoverIdx / (n - 1)
            // M6: clamp anchor so tooltip never clips at left or right edge
            val leftPct = rawPct.coerceIn(8.0, 92.0)
            val alignRight = leftPct > 60.0
            Box(
                Modifier
                    .position(Position.Absolute)
                    .top(8.px)
                    .styleModifier {
                        if (alignRight) {
                            property("right", "${fmt(100.0 - leftPct)}%")
                        } else {
                            property("left", "${fmt(leftPct)}%")
                        }
                        property("z-index", "10")
                        property("pointer-events", "none")
                    }
            ) {
                TooltipBox(labels[hoverIdx], series, hoverIdx)
            }
        }
    }
}

/** Dark-glass tooltip card, rendered as Compose layout. */
@Composable
private fun TooltipBox(label: String, series: List<ChartSeries>, idx: Int) {
    Div(attrs = {
        style {
            property("background", "rgba(10,14,24,.97)")
            property("border", "1px solid rgba(255,255,255,.12)")
            property("border-radius", "10px")
            property("padding", "8px 12px")
            property("min-width", "120px")
            property("font-family", "'Inter',sans-serif")
            property("pointer-events", "none")
        }
    }) {
        Div(attrs = {
            style {
                property("font-size", "11px")
                property("font-weight", "600")
                property("letter-spacing", ".12em")
                property("text-transform", "uppercase")
                property("color", "rgba(240,244,252,.55)")
                property("margin-bottom", "6px")
            }
        }) {
            org.jetbrains.compose.web.dom.Text(label)
        }
        series.forEach { s ->
            val v = s.values.getOrElse(idx) { 0.0 }
            if (!v.isNaN()) {
                Div(attrs = {
                    style {
                        property("display", "flex")
                        property("align-items", "center")
                        property("gap", "6px")
                        property("margin-top", "3px")
                    }
                }) {
                    Div(attrs = {
                        style {
                            property("width", "8px")
                            property("height", "8px")
                            property("border-radius", "2px")
                            property("background", s.colorHex)
                            property("flex-shrink", "0")
                        }
                    })
                    Div(attrs = {
                        style {
                            property("font-size", "11px")
                            property("color", "rgba(240,244,252,.85)")
                        }
                    }) {
                        org.jetbrains.compose.web.dom.Text("${s.name}: ${fmt(v)}")
                    }
                }
            }
        }
    }
}

// ── Pure SVG string builder ────────────────────────────────────────────────

private fun buildAreaChartSvg(
    series: List<ChartSeries>,
    labels: List<String>,
    heightPx: Int,
    annotations: List<ChartAnnotation>,
    hoverIdx: Int,
): String {
    val n = labels.size
    val validSeries = series.filter { it.values.isNotEmpty() }
    if (validSeries.isEmpty()) return ""

    val w = 1000.0
    val h = heightPx.toDouble()
    val padY = 14.0
    // H2: compute maxV only over the plotted take(n) slice so scale matches render
    val maxV = validSeries.flatMap { it.values.take(n) }.filter { !it.isNaN() }.maxOrNull()
        ?.coerceAtLeast(1.0) ?: 1.0

    fun xAt(i: Int) = if (n <= 1) 0.0 else w * i / (n - 1)
    fun yAt(v: Double): Double {
        if (v.isNaN()) return h - padY
        return h - padY - (h - 2 * padY) * (v / maxV).coerceIn(0.0, 1.0)
    }

    return buildString {
        append("<svg viewBox=\"0 0 ${w.toInt()} ${h.toInt()}\" preserveAspectRatio=\"none\"")
        append(" width=\"100%\" xmlns=\"http://www.w3.org/2000/svg\" style=\"display:block\">")

        // ── Defs: per-series gradient fills ──────────────────────────────
        append("<defs>")
        validSeries.forEachIndexed { si, s ->
            append("<linearGradient id=\"naag$si\" x1=\"0\" y1=\"0\" x2=\"0\" y2=\"1\">")
            append("<stop offset=\"0\" stop-color=\"${s.colorHex}\" stop-opacity=\"0.32\"/>")
            append("<stop offset=\"1\" stop-color=\"${s.colorHex}\" stop-opacity=\"0\"/>")
            append("</linearGradient>")
        }
        append("</defs>")

        // ── Subtle grid lines ──────────────────────────────────────────────
        val gridSteps = 3
        val step = (h - 2 * padY) / gridSteps
        append("<g stroke=\"rgba(255,255,255,.05)\" stroke-width=\"1\">")
        for (gi in 1..gridSteps) {
            val gy = fmt(padY + step * gi)
            append("<line x1=\"0\" y1=\"$gy\" x2=\"${w.toInt()}\" y2=\"$gy\"/>")
        }
        append("</g>")

        // ── Area fills + strokes per series ────────────────────────────────
        validSeries.forEachIndexed { si, s ->
            val safePts = s.values.take(n).map { if (it.isNaN()) 0.0 else it }
            val pxArr = DoubleArray(safePts.size) { i -> xAt(i) }
            val pyArr = DoubleArray(safePts.size) { i -> yAt(safePts[i]) }
            if (pxArr.isEmpty()) return@forEachIndexed
            val line = smoothPath(pxArr, pyArr)
            // H3: close area using the same pxArr end-points (explicit, not xAt(0))
            val firstX = fmt(pxArr[0])
            val lastX = fmt(pxArr[pxArr.size - 1])
            append("<path d=\"$line L $lastX ${fmt(h)} L $firstX ${fmt(h)} Z\"")
            append(" fill=\"url(#naag$si)\"/>")
            // Stroke
            append("<path d=\"$line\" fill=\"none\" stroke=\"${s.colorHex}\"")
            append(" stroke-width=\"2.4\" stroke-linecap=\"round\" stroke-linejoin=\"round\"/>")
        }

        // ── Terminal dot on first series ───────────────────────────────────
        validSeries.firstOrNull()?.let { s ->
            val lastIdx = s.values.take(n).size - 1
            if (lastIdx >= 0) {
                val lv = s.values.getOrElse(lastIdx) { 0.0 }.let { if (it.isNaN()) 0.0 else it }
                val lx = fmt(xAt(lastIdx))
                val ly = fmt(yAt(lv))
                append("<circle cx=\"$lx\" cy=\"$ly\" r=\"4\" fill=\"${s.colorHex}\"")
                append(" stroke=\"#05080F\" stroke-width=\"2\"/>")
            }
        }

        // ── Annotation markers ─────────────────────────────────────────────
        annotations.forEach { ann ->
            if (ann.index in 0 until n) {
                val ax = fmt(xAt(ann.index))
                append("<line x1=\"$ax\" y1=\"0\" x2=\"$ax\" y2=\"${fmt(h)}\"")
                append(" stroke=\"${ann.colorHex}\" stroke-opacity=\"0.55\" stroke-width=\"1\" stroke-dasharray=\"4 4\"/>")
                // Caption text near bottom
                val textAnchor = if (ann.index > n / 2) "end" else "start"
                append("<text x=\"${fmt(xAt(ann.index) + if (ann.index > n / 2) -6 else 6)}\"")
                append(" y=\"${fmt(h - 3)}\" text-anchor=\"$textAnchor\"")
                append(" font-size=\"9.5\" fill=\"${ann.colorHex}\"")
                append(" font-family=\"'JetBrains Mono',monospace\">")
                append(xmlEsc(ann.text))
                append("</text>")
            }
        }

        // ── Hover: vertical line + dots ────────────────────────────────────
        if (hoverIdx in 0 until n) {
            val hx = fmt(xAt(hoverIdx))
            append("<line x1=\"$hx\" y1=\"0\" x2=\"$hx\" y2=\"${fmt(h)}\"")
            append(" stroke=\"rgba(255,255,255,.18)\" stroke-width=\"1\"/>")
            validSeries.forEach { s ->
                val v = s.values.getOrElse(hoverIdx) { 0.0 }.let { if (it.isNaN()) 0.0 else it }
                append("<circle cx=\"$hx\" cy=\"${fmt(yAt(v))}\" r=\"3.6\"")
                append(" fill=\"${s.colorHex}\" stroke=\"#05080F\" stroke-width=\"1.5\"/>")
            }
        }

        append("</svg>")
    }
}

@Composable
private fun EmptyChartStub(heightPx: Int, modifier: Modifier) {
    Box(
        modifier
            .fillMaxWidth()
            .height(heightPx.px)
            .styleModifier {
                property("background", "rgba(255,255,255,.02)")
                property("border-radius", "8px")
            }
    )
}
